import { create, StateCreator } from "zustand";
import { createJSONStorage, persist, PersistOptions } from 'zustand/middleware';
import { logger } from '../utils/logger';

type RecommendStore = {
  totalPrice: number;
  totalCount: number;
  totalModifierPrice: number;
  orderPrice: number;
  orderCount: number;
  orderTotalPrice: number;
  checkedModifierPrice: (value: { price: number, type: string }) => void;
  calcOrderTotalPrice: (modifierPrice: number) => void;
  setOrderPrice: (value: number) => void;
  setOrderCount: (value: number) => void;
  resetOrder: () => void;
};

type MyPersist = (
  config: StateCreator<RecommendStore>,
  options: PersistOptions<RecommendStore>
) => StateCreator<RecommendStore>;

type State = {
  totalPrice: number;
  totalCount: number;
  totalModifierPrice: number;
  orderPrice: number;
  orderCount: number;
  orderTotalPrice: number;
};

const initialState: State = {
  totalPrice: 0,
  totalCount: 0,
  totalModifierPrice: 0,
  orderPrice: 0,
  orderCount: 1,
  orderTotalPrice: 0,
};

export const useRecommendStore = create<RecommendStore>(
  (persist as MyPersist)(
    (set, get): RecommendStore => ({
      ...initialState,
      calcOrderTotalPrice: (modifierPrice: number) => {
        if (typeof modifierPrice !== 'number' || modifierPrice < 0) {
          logger.error('Invalid modifierPrice value:', modifierPrice);
          return;
        }
        set((state) => {
          const totalPrice = (state.orderPrice + modifierPrice) * state.orderCount;
          logger.log('Calculated totalPrice:', totalPrice);
          return { orderTotalPrice: totalPrice };
        });
      },
      checkedModifierPrice: (value: { price: number, type: string }) => {
        if (typeof value.price !== 'number' || !['add', 'sub', 'set'].includes(value.type)) {
          logger.error('Invalid checkedModifierPrice value:', value);
          return;
        }
        set((state) => {
          let totalPrice = 0;
          if (value.type === 'add') {
            totalPrice = state.totalModifierPrice + value.price;
            logger.log('Add price:', totalPrice);
          } else if (value.type === 'sub') {
            totalPrice = state.totalModifierPrice - value.price;
            logger.log('Subtract price:', totalPrice);
          } else if (value.type === 'set') {
            totalPrice = value.price;
            logger.log('Set price:', totalPrice);
          }
          state.calcOrderTotalPrice(totalPrice);
          return { totalModifierPrice: totalPrice };
        });
      },
      setOrderPrice: (value: number) => {
        if (typeof value !== 'number' || value < 0) {
          logger.error('Invalid orderPrice value:', value);
          return;
        }
        set((state) => {
          state.orderPrice = value;
          state.calcOrderTotalPrice(state.totalModifierPrice);
          return { orderPrice: value };
        });
      },
      setOrderCount: (value: number) => {
        if (typeof value !== 'number' || value < 0) {
          logger.error('Invalid orderCount value:', value);
          return;
        }
        set((state) => {
          state.calcOrderTotalPrice(state.totalModifierPrice);
          return { orderCount: value };
        });
      },
      resetOrder: () => {
        logger.log('Resetting order to initial state');
        set({ ...initialState });
      },
    }),
    {
      name: 'recommend',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
